<template>
  <div class="tw:mt-3" :id="container"></div>
</template>

<script setup>
import { useStore } from 'vuex'
import { nextTick, watch, computed } from 'vue'
import Highcharts from 'highcharts'

const emit = defineEmits(['select-day'])
import * as Sentry from '@sentry/vue'

import { getTooltip, formatDate } from '@/components/rateInsights/utils.js'
import { useFormatting } from '@/composables/useFormatting'
import { selectedAccommodationId } from '@/layouts/queries'

const store = useStore()
const { formatCurrency } = useFormatting()
const props = defineProps({
  container: {
    type: String,
    required: true,
  },
  series: {
    type: Array,
    default() {
      return []
    },
  },
  categories: {
    type: Object,
    default() {
      return {}
    },
  },
  days: {
    type: Object,
    default() {
      return {}
    },
  },
  competitors: {
    type: Object,
    default() {
      return {}
    },
  },
  filters: {
    type: Object,
    default() {
      return {}
    },
  },
  translationKeys: {
    type: Object,
    default() {
      return {}
    },
  },
  currency: {
    type: String,
    default: '',
  },
})

const localization = computed(() => store.getters['getLanguage'])
const drawChart = (container, data, categories, days, competitors, filters, translationKeys, currency) => {
  if (!container || !data) return
  try {
    // without nextTick, highcharts may use the container before it exists in the DOM, it is important!
    nextTick(() => {
      // if for some reason the container is not yet mounted, go back.
      if (!document.getElementById(container)) return
      Highcharts.chart(container, {
        title: {
          text: null,
        },
        chart: {
          alignTicks: true,
          type: 'spline',
          backgroundColor: null,
          scrollablePlotArea: {
            minWidth: 700,
            scrollPositionX: 1,
          },
          plotBorderWidth: 0,
          style: {
            fontFamily: `SourceSansPro, Sans-Serif, "Sans Serif"`,
          },
        },
        credits: {
          enabled: false,
        },
        series: data,
        xAxis: {
          categories,
          gridLineColor: '#C4E3F4',
          lineColor: '#C4E3F4',
          tickColor: '#C4E3F4',
          labels: {
            style: { color: '#6C707A', fontSize: '14px' },
            formatter: function () {
              return formatDate(this.value, localization.value)
            },
          },
          tickLength: 0,
          gridLineWidth: 1,
          crosshair: {
            dashStyle: 'solid',
          },
        },
        yAxis: [
          {
            gridLineColor: '#C4E3F4',
            title: false,
            labels: {
              padding: 30,
              formatter: function () {
                return formatCurrency(this.value)
              },
            },
          },
        ],
        legend: {
          enabled: true,
          useHTML: true,
          align: 'left',
          verticalAlign: 'bottom',
          layout: 'horizontal',
          symbolWidth: 0,
          symbolPadding: 0,
          itemDistance: 20,
          labelFormatter: function () {
            const isOwnHotel = this.options.competitorId === selectedAccommodationId.value
            const nameStyle = isOwnHotel ? 'font-weight:bold;' : ''
            return `<span style="display:inline-flex;align-items:center;gap:6px;"><span style="width:14px;height:14px;border-radius:9999px;background:${this.color};display:inline-block;"></span><span style="${nameStyle}">${this.name}</span></span>`
          },
          itemStyle: {
            color: '#0A1121',
            fontSize: '14px',
          },
          symbolRadius: 9999,
        },
        plotOptions: {
          column: {
            maxPointWidth: 50,
          },
          series: {
            stickyTracking: true,
            allowPointSelect: true,
            connectNulls: true,
            color: '#FFFF00',
            animation: false,
            lineWidth: 2,
            marker: {
              enabled: true,
              radius: 3,
              states: {
                hover: {
                  radius: 4,
                },
              },
            },
            point: {
              events: {
                select: function (e) {
                  e.preventDefault()
                  emit('select-day', e.target.category.timestamp)
                },
              },
            },
          },
          marker: {
            lineColor: null,
            states: {
              select: {
                fillColor: null,
                radius: 8,
              },
            },
          },
        },
        tooltip: {
          useHTML: true,
          shadow: false,
          borderWidth: 0,
          backgroundColor: null,
          style: {
            padding: 0,
          },
          outside: true,
          shared: false,
          formatter: function () {
            const colors = ['#007BC1', '#8291D3', '#FC9E15', '#599A19', '#FF8381', '#3F57BD']
            const point = this.point
            point.colorIndex = colors.indexOf(point.color)

            return getTooltip({
              ...this,
              params: {
                competitors: competitors,
                currency: currency,
                locale: localization.value,
                filterValues: filters,
                translationKeys: translationKeys,
                point: point,
                colors: colors,
              },
            })
          },
        },
      })
    })
  } catch (error) {
    Sentry.captureException(error)
  }
}

watch(
  () => props,
  newProps => {
    if (newProps.series?.length > 0 && newProps.container) {
      drawChart(
        newProps.container,
        newProps.series,
        newProps.categories,
        newProps.days,
        newProps.competitors,
        newProps.filters,
        newProps.translationKeys,
        newProps.currency
      )
    }
  },
  { deep: true, immediate: true }
)
</script>
